import React, { useEffect, useState } from 'react';
import { Check, Circle, Eye, EyeOff } from 'lucide-react';
import {
  BrandAnchor,
  ForgotPasswordLink,
  FormSection,
  Heading,
  LoginCard,
  LoginContainer,
  PasswordHeader,
  PasswordInputWrapper,
  PasswordLabel,
  PasswordToggleButton,
  SignInButton,
  SignUpText,
} from './AdminLogin.styles';
import { Input } from '../../../components/uielements/input';
import { PageWrapper } from '../../GeneralPages/landingPages/components/shared.styles';
import {
  resetPassword,
  forgotPassword,
  resendVerificationCode,
  confirmUser,
} from '../../../services/auth';
import { useAuth } from '../../../hooks/useAuth';
import { validateEmail, validatePassword } from '../../../helper/validationHelper';

import { ThemeProvider } from 'styled-components';
import { darkTheme, lightTheme } from '../../../settings/appStyle/theme';
import { AdminNavbar } from '../AdminNavbar';
import { checkPasswordPolicy, isPasswordPolicyMet } from '../../../helper/passwordpolicyHelper';
import {
  FieldRow,
  ForgotPasswordCard,
  PolicyChip,
  PolicyChips,
} from '../../UserPages/AuthPages/Login.styles';
import { RequiredLabel } from '../../UserPages/AuthPages/Login';

// ─── helpers ────────────────────────────────────────────────────────────────

const HelperText = ({ children }) => (
  <span
    role="alert"
    style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}
  >
    {children}
  </span>
);

const PasswordVisibilityToggle = ({ visible, onToggle }) => (
  <PasswordToggleButton
    type="button"
    onClick={onToggle}
    aria-label={visible ? 'Hide password' : 'Show password'}
    tabIndex={0}
  >
    {visible ? <EyeOff size={17} /> : <Eye size={17} />}
  </PasswordToggleButton>
);

// ─── Root router ────────────────────────────────────────────────────────────

const AdminLogin = () => {
  const [formState, setFormState] = useState('sign-in'); // 'sign-in' | 'forgot-password'

  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('theme');

    if (savedTheme) {
      return savedTheme === 'dark';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark((prev) => !prev);
  };

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <>
        <AdminNavbar isDark={isDark} toggleTheme={toggleTheme} />

        {formState === 'sign-in' && (
          <AdminSignIn
            onGoSignUp={() => setFormState('sign-up')}
            onGoForgotPassword={() => setFormState('forgot-password')}
          />
        )}

        {formState === 'forgot-password' && (
          <AdminForgotPassword onGoSignIn={() => setFormState('sign-in')} />
        )}
      </>{' '}
    </ThemeProvider>
  );
};

export default AdminLogin;

// ─── Sign In ────────────────────────────────────────────────────────────────

const AdminSignIn = ({ onGoForgotPassword }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);

  const validate = () => {
    const next = { email: '', password: '' };
    if (!validateEmail(email)) next.email = 'Please enter a valid email address.';
    if (!validatePassword(password)) next.password = 'Please enter your password.';
    setErrors(next);
    return !next.email && !next.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setIsLoading(true);
      await login(email, password, true); // Pass true for isAdmin
    } catch (error) {
      setIsLoading(false);
      if (error?.response?.data?.message === 'User Not Confirmed!') {
        alert('Please verify your email address to continue.');
        setPendingVerification(true);
      } else if (error?.response?.data?.detail === 'User not verified. Please verify your email.') {
        alert('Please verify your email address to continue.');
        setPendingVerification(true);
      } else {
        alert('Credentials not found. Please check your email and password.');
      }
    }
  };

  if (pendingVerification) {
    return (
      <EmailVerification
        email={email}
        onVerificationSuccess={() => setPendingVerification(false)}
      />
    );
  }

  return (
    <PageWrapper>
      <LoginContainer>
        <LoginCard>
          <BrandAnchor>
            <Heading>Admin Sign In</Heading>
          </BrandAnchor>

          <FormSection onSubmit={handleSubmit}>
            <div style={{ width: '100%' }}>
              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((p) => ({ ...p, email: '' }));
                }}
                iconSize="16.667px"
              />
              {errors.email && <HelperText>{errors.email}</HelperText>}
            </div>

            <div style={{ width: '100%' }}>
              <PasswordHeader>
                <PasswordLabel htmlFor="password">Password</PasswordLabel>
                <ForgotPasswordLink
                  as="span"
                  role="button"
                  tabIndex={0}
                  onClick={onGoForgotPassword}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onGoForgotPassword()}
                  style={{ cursor: 'pointer' }}
                >
                  Forgot password?
                </ForgotPasswordLink>
              </PasswordHeader>
              <PasswordInputWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((p) => ({ ...p, password: '' }));
                  }}
                  iconSize="13.333px"
                />
                <PasswordVisibilityToggle
                  visible={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />
              </PasswordInputWrapper>
              {errors.password && <HelperText>{errors.password}</HelperText>}
            </div>

            <SignInButton type="submit" disabled={isLoading}>
              {isLoading ? 'Signing in…' : 'Sign In'}
            </SignInButton>
          </FormSection>
        </LoginCard>
      </LoginContainer>
    </PageWrapper>
  );
};

// ─── Email Verification ──────────────────────────────────────────────────────

const EmailVerification = ({ email, onVerificationSuccess }) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      alert('Please enter the verification code.');
      return;
    }
    try {
      setIsLoading(true);
      await confirmUser(email, code);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onVerificationSuccess();
      }, 2500);
    } catch {
      alert('Verification failed. Please check your code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setIsResending(true);
      await resendVerificationCode(email);
      alert('Verification code sent! Please check your email.');
    } catch {
      alert('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <PageWrapper>
      <LoginContainer>
        <LoginCard>
          <BrandAnchor>
            <Heading>{success ? '✓ Verified!' : 'Verify Email'}</Heading>
          </BrandAnchor>

          {!success && (
            <FormSection onSubmit={handleVerify}>
              <div style={{ width: '100%' }}>
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  disabled
                  iconSize="16.667px"
                />
              </div>

              <div style={{ width: '100%' }}>
                <Input
                  label="Verification Code"
                  type="text"
                  name="verificationCode"
                  placeholder="Enter the code from your email"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  iconSize="16.667px"
                />
              </div>

              <SignInButton type="submit" disabled={isLoading}>
                {isLoading ? 'Verifying…' : 'Verify'}
              </SignInButton>
            </FormSection>
          )}

          {!success && (
            <SignUpText>
              Didn&apos;t receive a code?{' '}
              <span
                role="button"
                tabIndex={0}
                onClick={!isResending ? handleResend : undefined}
                onKeyDown={(e) =>
                  !isResending && (e.key === 'Enter' || e.key === ' ') && handleResend()
                }
                style={{
                  cursor: isResending ? 'default' : 'pointer',
                  opacity: isResending ? 0.6 : 1,
                }}
              >
                {isResending ? 'Sending…' : 'Resend Code'}
              </span>
            </SignUpText>
          )}

          {success && (
            <p style={{ textAlign: 'center', color: '#38a169', marginTop: '16px' }}>
              Email verified successfully! Redirecting to sign in…
            </p>
          )}
        </LoginCard>
      </LoginContainer>
    </PageWrapper>
  );
};

// ─── Forgot / Reset Password ─────────────────────────────────────────────────

const AdminForgotPassword = ({ onGoSignIn }) => {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false); // false = "get code" step, true = "reset" step

  const policyResults = checkPasswordPolicy(newPassword);
  const policyMet = isPasswordPolicyMet(newPassword);
  const passwordsMatch = newPassword === confirmPassword;
  const showMismatch = confirmPassword.length > 0 && !passwordsMatch;
  const canSubmitReset =
    policyMet && confirmPassword !== '' && passwordsMatch && confirmationCode.trim() !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!codeSent) {
      // Step 1 — request reset code
      if (!validateEmail(email)) {
        setErrors({ email: 'Please enter a valid email address.' });
        setIsLoading(false);
        return;
      }
      try {
        await forgotPassword(email);
        setCodeSent(true);
      } catch (error) {
        alert(error?.message);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Step 2 — confirm new password
      const next = {};
      if (!newPassword) next.password = 'Password is required.';
      else if (!policyMet) next.password = 'Password does not meet the policy.';
      if (!confirmPassword) next.confirmPassword = 'Confirm password is required.';
      else if (!passwordsMatch) next.confirmPassword = 'Passwords do not match.';
      if (!confirmationCode.trim()) next.confirmationCode = 'Please enter the confirmation code.';
      if (Object.keys(next).length) {
        setErrors(next);
        setIsLoading(false);
        return;
      }
      try {
        await resetPassword(email, newPassword, confirmationCode);
        alert('Password reset successful. Please sign in.');
        onGoSignIn();
      } catch {
        alert('Reset failed. Please check your code and try again.');
        setCodeSent(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <PageWrapper>
      <LoginContainer>
        <ForgotPasswordCard>
          <BrandAnchor>
            <Heading>Forgot Password</Heading>
          </BrandAnchor>

          <FormSection onSubmit={handleSubmit}>
            <div style={{ width: '100%' }}>
              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((p) => ({ ...p, email: '' }));
                }}
                disabled={codeSent}
                iconSize="16.667px"
              />
              {errors.email && <HelperText>{errors.email}</HelperText>}
            </div>

            {codeSent && (
              <>
                <FieldRow>
                  <div style={{ minWidth: 0 }}>
                    <PasswordHeader>
                      <PasswordLabel htmlFor="new-password">
                        <RequiredLabel>New Password</RequiredLabel>
                      </PasswordLabel>
                    </PasswordHeader>
                    <PasswordInputWrapper>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        id="new-password"
                        name="password"
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setErrors((p) => ({ ...p, password: '' }));
                        }}
                        iconSize="13.333px"
                        autoComplete="new-password"
                      />
                      <PasswordVisibilityToggle
                        visible={showPassword}
                        onToggle={() => setShowPassword((v) => !v)}
                      />
                    </PasswordInputWrapper>
                    {errors.password && <HelperText>{errors.password}</HelperText>}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <PasswordHeader>
                      <PasswordLabel htmlFor="confirm-new-password">
                        <RequiredLabel>Confirm Password</RequiredLabel>
                      </PasswordLabel>
                    </PasswordHeader>
                    <PasswordInputWrapper>
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirm-new-password"
                        name="confirmPassword"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setErrors((p) => ({ ...p, confirmPassword: '' }));
                        }}
                        iconSize="13.333px"
                        autoComplete="new-password"
                      />
                      <PasswordVisibilityToggle
                        visible={showConfirmPassword}
                        onToggle={() => setShowConfirmPassword((v) => !v)}
                      />
                    </PasswordInputWrapper>
                    {(showMismatch || errors.confirmPassword) && (
                      <HelperText>
                        {showMismatch ? 'Passwords do not match.' : errors.confirmPassword}
                      </HelperText>
                    )}
                  </div>
                </FieldRow>

                {newPassword.length > 0 && (
                  <PolicyChips aria-live="polite">
                    {policyResults.map((rule) => (
                      <PolicyChip key={rule.id} $passed={rule.passed}>
                        {rule.passed ? <Check size={12} /> : <Circle size={12} />}
                        <span>{rule.label}</span>
                      </PolicyChip>
                    ))}
                  </PolicyChips>
                )}

                <div style={{ width: '100%' }}>
                  <Input
                    label={<RequiredLabel>Confirmation Code</RequiredLabel>}
                    type="text"
                    name="confirmationCode"
                    placeholder="Enter code from your email"
                    value={confirmationCode}
                    onChange={(e) => {
                      setConfirmationCode(e.target.value);
                      setErrors((p) => ({ ...p, confirmationCode: '' }));
                    }}
                    iconSize="16.667px"
                  />
                  {errors.confirmationCode && <HelperText>{errors.confirmationCode}</HelperText>}
                </div>
              </>
            )}

            <SignInButton type="submit" disabled={isLoading || (codeSent && !canSubmitReset)}>
              {isLoading ? 'Please wait…' : codeSent ? 'Reset Password' : 'Get Code'}
            </SignInButton>
          </FormSection>

          <SignUpText>
            <span
              role="button"
              tabIndex={0}
              onClick={onGoSignIn}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onGoSignIn()}
              style={{ cursor: 'pointer' }}
            >
              ← Back to Sign In
            </span>
          </SignUpText>
        </ForgotPasswordCard>
      </LoginContainer>
    </PageWrapper>
  );
};
